// src/client/services/ModalManager.js
import { reactive, markRaw } from "vue";
import ModalConfirm from "@/components/modal/ModalConfirm.vue";

let activeInstance = null;

/**
 * Global reactive modal state
 *
 * Backward compatible:
 *  - state.visible
 *  - state.component
 *  - state.props
 *  - etc.
 *
 * New:
 *  - state.stack[] for modal stacking
 */
const modalState = reactive({
  // legacy single-modal bindings (always reflect TOP modal)
  visible: false,
  title: "",
  component: null,
  props: {},
  record: null,
  size: "md",
  detectValidation: null,
  onClose: null,
  instance: null,

  // NEW: modal stack
  stack: []
});

function syncTopToLegacyState() {
  const top = modalState.stack.at(-1);

  if (!top) {
    modalState.visible = false;
    modalState.component = null;
    modalState.props = {};
    modalState.record = null;
    modalState.title = "";
    modalState.size = "md";
    modalState.detectValidation = null;
    modalState.onClose = null;
    modalState.instance = null;
    return;
  }

  modalState.visible = true;
  modalState.component = top.component;
  modalState.props = top.props;
  modalState.record = top.record;
  modalState.title = top.title;
  modalState.size = top.size;
  modalState.detectValidation = top.detectValidation;
  modalState.onClose = top.onClose;

  // instance mirrors the current active instance
  modalState.instance = activeInstance;
}

function createEntry(opts = {}) {
  return {
    title: opts.title ?? "",
    component: opts.component ? markRaw(opts.component) : null,
    props: opts.props ?? {},
    record: opts.record ?? null,
    size: opts.size ?? "md",
    detectValidation: opts.detectValidation ?? null,
    onClose: opts.onClose ?? null
  };
}

export default {

  state: modalState,

  /**
   * Called by modal component onMounted
   * Applies ONLY to the top modal
   */
  setChildInstance(instance) {
    const top = modalState.stack.at(-1);
    if (!top) return;

    // only accept if this instance belongs to the top modal
    activeInstance = instance;
    modalState.instance = activeInstance;
  
  },

  clearChildInstance(instance) {
    if (activeInstance === instance) {
      activeInstance = null;
      modalState.instance = null;
    }
  },

  getActiveInstance() {
    return activeInstance;
  },

  /**
   * Open modal
   */
  open(options, maybeProps = {}, maybeTitle = "") {

    // -----------------------------
    // LEGACY MODE
    // -----------------------------
    if (typeof options === "function" || (options && options.__isVue)) {
      modalState.stack.push(
        createEntry({
          component: options,
          props: maybeProps || {},
          title: maybeTitle || "",
          record: maybeProps?.record ?? null
        })
      );
      syncTopToLegacyState();
      return;
    }

    // -----------------------------
    // NEW API
    // -----------------------------
    modalState.stack.push(
      createEntry(options || {})
    );

    syncTopToLegacyState();
  },

  /**
   * Close TOP modal only
   */
  close(payload) {
    const modal = modalState.stack.pop();
    if (!modal) return;

    if (typeof modal.onClose === "function") {
      try {
        modal.onClose(payload);
      } catch (err) {
        console.warn("Modal onClose callback error:", err);
      }
    }

    activeInstance = null;
    modalState.instance = null;

    syncTopToLegacyState();
  },

  confirm(opts) {
    return new Promise(resolve => {
      this.open({
        component: ModalConfirm,
        size: "sm",
        ...opts,
        onClose: resolve
      });
    });
  }
};


